// src/features/dashboard/pages/DashboardPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../../shared/api/apiClient";
import styles from "./DashboardPage.module.css";

import ReactApexChart from "react-apexcharts";

/* -------------------- Rate Limiting -------------------- */
import RateLimitsInfo from "../../../components/RateLimit/RateLimitsInfo";
import { getUserPermissions, getUserRole } from "../../../shared/lib/auth/authHelper";

/* -------------------- helpers -------------------- */
const LS_KEY = "dash:v7:filters"; // bump again (owner control changed)
const toNum = (v) => (v == null ? 0 : Number(v) || 0);

const formatDateTime = (value, t) => {
  if (!value) return t?.("n_a") || "N/A";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

function toISODate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function lastNDaysRange(n) {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (n - 1));
  return { from: toISODate(from), to: toISODate(to) };
}

function safeParseJSON(str, fallback) {
  try {
    const v = JSON.parse(str);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function calcDeltaPct(arr) {
  if (!Array.isArray(arr) || arr.length < 2) return null;
  const half = Math.max(1, Math.floor(arr.length / 2));
  const a = arr.slice(0, half).reduce((sum, v) => sum + toNum(v), 0);
  const b = arr.slice(half).reduce((sum, v) => sum + toNum(v), 0);
  if (a === 0) return b === 0 ? 0 : 100;
  return ((b - a) / a) * 100;
}

/* -------------------- charts -------------------- */
function getCssVar(name, fallback) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

/* ✅ robust dark theme detection */
function getIsDarkTheme() {
  const el = document.documentElement;
  const body = document.body;

  const dt = (el?.getAttribute("data-theme") || body?.getAttribute("data-theme") || "").toLowerCase();
  if (dt === "dark") return true;

  const bs = (el?.getAttribute("data-bs-theme") || body?.getAttribute("data-bs-theme") || "").toLowerCase();
  if (bs === "dark") return true;

  if (el?.classList?.contains("dark") || body?.classList?.contains("dark")) return true;
  if (el?.classList?.contains("theme-dark") || body?.classList?.contains("theme-dark")) return true;

  return false;
}

/* -------------------- activity helpers -------------------- */
function stripEnumPrefix(v) {
  if (v == null) return "";
  const s = String(v);
  return s.replace(/^ActivityLogEnum\./, "").replace(/^EntityEnum\./, "");
}

function prettyAction(v, t) {
  const raw = stripEnumPrefix(v).toLowerCase();
  if (!raw) return t?.("n_a") || "N/A";
  if (raw.includes("create")) return t?.("activity_action_create") || "Created";
  if (raw.includes("edit") || raw.includes("update")) return t?.("activity_action_update") || "Updated";
  if (raw.includes("delete") || raw.includes("remove")) return t?.("activity_action_delete") || "Deleted";
  return raw
    .replace(/_/g, " ")
    .replace(/\./g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function prettyEntity(v, t) {
  const raw = stripEnumPrefix(v).toLowerCase();
  if (!raw) return t?.("n_a") || "N/A";

  const map = {
    leads: t?.("activity_entity_leads") || "Leads",
    deals: t?.("activity_entity_deals") || "Deals",
    tasks: t?.("activity_entity_tasks") || "Tasks",
    sources: t?.("activity_entity_sources") || "Sources",
    users: t?.("activity_entity_users") || "Users",
    companies: t?.("activity_entity_companies") || "Companies",
    contacts: t?.("activity_entity_contacts") || "Contacts",
    notes: t?.("activity_entity_notes") || "Notes",
    pipeline_stages: t?.("activity_entity_pipeline_stages") || "Pipeline stages",
  };

  if (map[raw]) return map[raw];
  const hit = Object.keys(map).find((k) => raw.includes(k));
  if (hit) return map[hit];

  return raw
    .replace(/_/g, " ")
    .replace(/\./g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/* -------------------- Owner searchable select (single field; search INSIDE dropdown) -------------------- */
function normalizeStr(v) {
  return (v == null ? "" : String(v)).toLowerCase().trim();
}

/**
 * options: [{ ui_id, full_name, email, userId }]
 * value: selected ui_id string or ""
 */
function OwnerSearchSelect({
  t,
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled,
  loading,
  errorText,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const selected = useMemo(() => {
    const v = String(value || "").trim();
    if (!v) return null;
    return (options || []).find((o) => String(o.ui_id) === v) || null;
  }, [options, value]);

  const selectedLabel = useMemo(() => {
    if (!selected) return "";
    const nm = selected.full_name || (t("account_user_fallback") || "User");
    const em = selected.email ? ` (${selected.email})` : "";
    return `${selected.ui_id} — ${nm}${em}`;
  }, [selected, t]);

  const filtered = useMemo(() => {
    const qq = normalizeStr(q);
    if (!qq) return options || [];
    return (options || []).filter((o) => {
      const a = normalizeStr(o?.ui_id);
      const b = normalizeStr(o?.full_name);
      const c = normalizeStr(o?.email);
      return a.includes(qq) || b.includes(qq) || c.includes(qq);
    });
  }, [options, q]);

  useEffect(() => {
    setActiveIdx(0);
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!open) return;
      const root = rootRef.current;
      if (!root) return;
      if (!root.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closeDropdown = () => setOpen(false);

  const pick = (uiId) => {
    onChange?.(uiId ? String(uiId) : "");
    setQ("");
    closeDropdown();
  };

  const clear = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    pick("");
  };

  const onKeyDownClosed = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      openDropdown();
    }
    if (e.key === "Backspace" && value) {
      e.preventDefault();
      pick("");
    }
  };

  const onKeyDownSearch = (e) => {
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const it = filtered[activeIdx];
      if (it) pick(it.ui_id);
      return;
    }
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <div className={styles.label}>{label}</div>

      <div
        className="form-control"
        role="combobox"
        aria-expanded={open ? "true" : "false"}
        tabIndex={disabled ? -1 : 0}
        onClick={() => (open ? closeDropdown() : openDropdown())}
        onKeyDown={onKeyDownClosed}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          cursor: disabled ? "not-allowed" : "pointer",
          userSelect: "none",
          opacity: disabled ? 0.7 : 1,
        }}
        title={selectedLabel || ""}
      >
        <div
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selected ? "inherit" : "var(--muted)",
          }}
        >
          {selected ? selectedLabel : placeholder}
        </div>

        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
          {value ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={clear}
              disabled={disabled}
              style={{ padding: "0 6px", lineHeight: "18px" }}
              title={t("clear") || "Clear"}
            >
              <i className="lni lni-xmark"></i>
            </button>
          ) : null}
          <span style={{ opacity: 0.85 }}><i class="lni lni-chevron-down"></i></span>
        </div>
      </div>

      {open ? (
        <div
          className="dropdown-menu show"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 6px)",
            zIndex: 2000,
            padding: 8,
            maxHeight: 320,
            overflow: "hidden",
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            placeholder={t("search") || "Search..."}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDownSearch}
            disabled={disabled}
            style={{ marginBottom: 8 }}
          />

          <div style={{ maxHeight: 250, overflow: "auto" }}>
            {loading ? (
              <div className="text-muted px-2 py-1">{(t("loading") || "Loading") + "..."}</div>
            ) : errorText ? (
              <div className="text-danger px-2 py-1">{String(errorText)}</div>
            ) : filtered.length === 0 ? (
              <div className="text-muted px-2 py-1">{t("dashboard_empty") || "Empty"}</div>
            ) : (
              filtered.map((o, idx) => {
                const nm = o.full_name || (t("account_user_fallback") || "User");
                const em = o.email ? ` (${o.email})` : "";
                const lbl = `${o.ui_id} — ${nm}${em}`;
                const active = idx === activeIdx;

                return (
                  <button
                    key={`owner-opt-${o.ui_id}`}
                    type="button"
                    className="dropdown-item"
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => pick(o.ui_id)}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      background: active ? "rgba(59,130,246,0.18)" : "transparent",
                      borderRadius: 6,
                    }}
                    title={lbl}
                  >
                    {lbl}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------- Spark KPI -------------------- */
function SparkKpi({ title, value, suffix = "", deltaPct = null, data = [], colorVar = "--c1", t }) {
  const [darkMode, setDarkMode] = useState(getIsDarkTheme());

  useEffect(() => {
    const el = document.documentElement;
    const body = document.body;

    const apply = () => setDarkMode(getIsDarkTheme());
    apply();

    const obs = new MutationObserver(() => apply());
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme", "data-bs-theme", "class"] });
    if (body) obs.observe(body, { attributes: true, attributeFilter: ["data-theme", "data-bs-theme", "class"] });

    return () => obs.disconnect();
  }, []);

  const color = useMemo(() => getCssVar(colorVar, "rgba(91, 140, 255, 0.95)"), [colorVar]);
  const up = deltaPct != null && deltaPct >= 0;

  const series = useMemo(() => [{ name: title, data: (data || []).map((x) => toNum(x)) }], [data, title]);

  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 46,
        sparkline: { enabled: true },
        animations: { enabled: true },
        toolbar: { show: false },
        background: "transparent",
      },
      theme: { mode: darkMode ? "dark" : "light" },
      stroke: { curve: "smooth", width: 2.2 },
      fill: { opacity: 0.22 },
      dataLabels: { enabled: false },
      tooltip: { enabled: false },
      colors: [color],
    }),
    [color, darkMode]
  );

  return (
    <div
      className={styles.kpi}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "10px 12px",
        minWidth: 0,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className={styles.kpiLbl}>{title}</div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
          <div className={styles.kpiVal} style={{ marginTop: 2 }}>
            {value}
          </div>
          {suffix ? (
            <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 650, marginBottom: 2 }}>{suffix}</div>
          ) : null}
        </div>

        {deltaPct == null ? null : (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              fontWeight: 650,
              color: up ? "rgba(34,197,94,0.95)" : "rgba(239,68,68,0.95)",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
            aria-label={t?.("dashboard_trend") || "Trend"}
          >
            {up ? "▲" : "▼"} {Math.abs(deltaPct).toFixed(1)}%
          </div>
        )}
      </div>

      <div style={{ width: 130, flex: "0 0 auto", opacity: 0.95 }}>
        <ReactApexChart options={options} series={series} type="area" height={46} />
      </div>
    </div>
  );
}

/* -------------------- chart -------------------- */
function AreaOverview({ title, series, t }) {
  const [colors, setColors] = useState([
    "rgba(91, 140, 255, 0.95)",
    "rgba(56, 189, 248, 0.85)",
    "rgba(168, 85, 247, 0.75)",
  ]);

  const [darkMode, setDarkMode] = useState(getIsDarkTheme());
  const chartKey = darkMode ? "chart-dark" : "chart-light";

  useEffect(() => {
    const c1 = getCssVar("--c1", "rgba(91, 140, 255, 0.95)");
    const c2 = getCssVar("--c2", "rgba(56, 189, 248, 0.85)");
    const c3 = getCssVar("--c3", "rgba(168, 85, 247, 0.75)");
    setColors([c1, c2, c3]);

    const el = document.documentElement;
    const body = document.body;

    const apply = () => setDarkMode(getIsDarkTheme());
    apply();

    const obs = new MutationObserver(() => apply());

    obs.observe(el, { attributes: true, attributeFilter: ["data-theme", "data-bs-theme", "class"] });
    if (body) obs.observe(body, { attributes: true, attributeFilter: ["data-theme", "data-bs-theme", "class"] });

    return () => obs.disconnect();
  }, []);

  const leadsLabel = t?.("dashboard_leads") || "Leads";
  const wonLabel = t?.("dashboard_won_eur") || "Won €";
  const tasksLabel = t?.("dashboard_tasks") || "Tasks";

  const apexSeries = useMemo(() => {
    const pts = (series || []).map((x) => {
      const dt = new Date(x.day);
      const tms = Number.isNaN(dt.getTime()) ? Date.now() : dt.getTime();
      return {
        t: tms,
        leads: toNum(x.leads_created),
        won: toNum(x.deals_won_amount),
        tasks: toNum(x.tasks_created),
      };
    });

    return [
      { name: leadsLabel, data: pts.map((p) => [p.t, p.leads]) },
      { name: wonLabel, data: pts.map((p) => [p.t, p.won]) },
      { name: tasksLabel, data: pts.map((p) => [p.t, p.tasks]) },
    ];
  }, [series, leadsLabel, wonLabel, tasksLabel]);

  const { maxCount, maxMoney } = useMemo(() => {
    const pts = (series || []).map((x) => ({
      leads: toNum(x.leads_created),
      tasks: toNum(x.tasks_created),
      money: toNum(x.deals_won_amount),
    }));
    const maxCountRaw = Math.max(1, ...pts.map((p) => Math.max(p.leads, p.tasks)));
    const maxMoneyRaw = Math.max(1, ...pts.map((p) => p.money));
    const pad = (v) => Math.ceil(v * 1.15);
    return { maxCount: pad(maxCountRaw), maxMoney: pad(maxMoneyRaw) };
  }, [series]);

  const options = useMemo(() => {
    const labelColor = darkMode ? "rgba(233,238,245,0.92)" : "rgba(15,23,42,0.70)";
    const gridColor = darkMode ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.10)";

    return {
      theme: { mode: darkMode ? "dark" : "light" },
      chart: {
        key: chartKey,
        type: "area",
        height: 220,
        background: "transparent",
        foreColor: labelColor,
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: false,
          zoomedArea: {
            fill: { color: "rgba(91,140,255,0.15)" },
            stroke: { color: "rgba(91,140,255,0.45)" },
          },
        },
        animations: { enabled: true },
      },

      colors,

      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2.5 },

      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0.4,
          opacityFrom: 0.33,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },

      markers: { size: 0, hover: { size: 5 } },

      grid: {
        borderColor: gridColor,
        strokeDashArray: 3,
        padding: { left: 10, right: 10, top: 6, bottom: 0 },
      },

      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          style: {
            colors: labelColor,
            fontSize: "12px",
            fontFamily: "inherit",
            fontWeight: 500,
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: true },
      },

      yaxis: [
        {
          seriesName: leadsLabel,
          min: 0,
          max: maxCount,
          tickAmount: 4,
          labels: {
            style: { colors: labelColor, fontSize: "12px", fontFamily: "inherit", fontWeight: 500 },
            formatter: (v) => Math.round(toNum(v)).toLocaleString(),
          },
        },
        {
          seriesName: wonLabel,
          opposite: true,
          min: 0,
          max: maxMoney,
          tickAmount: 4,
          labels: {
            style: { colors: labelColor, fontSize: "12px", fontFamily: "inherit", fontWeight: 500 },
            formatter: (v) => `${Math.round(toNum(v)).toLocaleString()} €`,
          },
        },
        {
          seriesName: tasksLabel,
          show: false,
          min: 0,
          max: maxCount,
        },
      ],

      tooltip: {
        shared: true,
        intersect: false,
        followCursor: true,
        theme: darkMode ? "dark" : "light",
        x: { format: "dd MMM" },
        style: { fontSize: "12px", fontFamily: "inherit" },
        y: {
          formatter: (val, opts) => {
            if (opts?.seriesIndex === 1) {
              return `${toNum(val).toLocaleString(undefined, { maximumFractionDigits: 0 })} €`;
            }
            return toNum(val).toLocaleString();
          },
        },
      },

      legend: { show: false },
    };
  }, [colors, darkMode, maxCount, maxMoney, leadsLabel, wonLabel, tasksLabel, chartKey]);

  return (
    <div className={`${styles.card} ${styles.overviewCard} ${styles.stretchCard}`}>
      <div className={styles.cardHead}>
        <div className={styles.cardTitle}>{title}</div>
        <div className={styles.overLegend}>
          <span>
            <i className={styles.l1} /> {leadsLabel}
          </span>
          <span>
            <i className={styles.l2} /> {wonLabel}
          </span>
          <span>
            <i className={styles.l3} /> {tasksLabel}
          </span>
        </div>
      </div>

      <div className={styles.overBody}>
        <ReactApexChart options={options} series={apexSeries} type="area" height={220} />
      </div>
    </div>
  );
}

/* -------------------- charts (SVG donuts/bars) -------------------- */
function Donut({ title, total, items, t }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [tip, setTip] = useState({ show: false, x: 0, y: 0, label: "", value: 0, pct: 0 });

  const size = 200;
  const cx = size / 2;
  const cy = size / 2;

  const rOuter = 78;
  const thickness = 26;
  const rInner = rOuter - thickness;

  const gapDeg = 0;

  const sum = useMemo(() => items.reduce((s, x) => s + toNum(x.value), 0) || 1, [items]);
  const colors = ["var(--c1)", "var(--c2)", "var(--c3)", "var(--c4)"];

  const deg2rad = (deg) => (deg * Math.PI) / 180;

  const polar = (radius, angleDeg) => {
    const a = deg2rad(angleDeg);
    return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) };
  };

  const segPath = (startDeg, endDeg) => {
    const p1 = polar(rOuter, startDeg);
    const p2 = polar(rOuter, endDeg);
    const p3 = polar(rInner, endDeg);
    const p4 = polar(rInner, startDeg);

    const large = endDeg - startDeg > 180 ? 1 : 0;

    return [
      `M ${p1.x} ${p1.y}`,
      `A ${rOuter} ${rOuter} 0 ${large} 1 ${p2.x} ${p2.y}`,
      `L ${p3.x} ${p3.y}`,
      `A ${rInner} ${rInner} 0 ${large} 0 ${p4.x} ${p4.y}`,
      "Z",
    ].join(" ");
  };

  const angleBudget = useMemo(() => {
    const n = Math.max(1, items.length);
    return Math.max(0, 360 - n * gapDeg);
  }, [items.length]);

  const segs = useMemo(() => {
    let a = -90;
    return items
      .map((it, idx) => {
        const v = toNum(it.value);
        if (v <= 0) return null;

        const frac = v / sum;
        const sweep = frac * angleBudget;

        const start = a;
        const end = a + sweep;
        const mid = (start + end) / 2;

        a = end + gapDeg;

        return {
          label: String(it.label ?? ""),
          value: v,
          pct: frac * 100,
          idx,
          start,
          end,
          mid,
          color: colors[idx % colors.length],
          path: segPath(start, end),
        };
      })
      .filter(Boolean);
  }, [items, sum, angleBudget]);

  const updateTip = (e, idx) => {
    const root = wrapRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const s = segs.find((z) => z.idx === idx);
    if (!s) return;

    setTip({
      show: true,
      x,
      y,
      label: String(s.label ?? ""),
      value: toNum(s.value),
      pct: toNum(s.pct),
    });
  };

  const hideTip = () => {
    setHoverIdx(null);
    setTip((p) => ({ ...p, show: false }));
  };

  const explode = (midDeg, amount = 10) => {
    const a = deg2rad(midDeg);
    return { dx: Math.cos(a) * amount, dy: Math.sin(a) * amount };
  };

  return (
    <div className={`${styles.card} ${styles.stretchCard}`}>
      <div className={styles.cardHead}>
        <div className={styles.cardTitle}>{title}</div>
      </div>

      <div ref={wrapRef} className={styles.donutWrap} style={{ position: "relative" }}>
        {tip.show ? (
          <div
            style={{
              position: "absolute",
              left: Math.min(Math.max(8, tip.x + 12), 240),
              top: Math.max(8, tip.y + 12),
              zIndex: 10,
              pointerEvents: "none",
              padding: "8px 10px",
              borderRadius: 10,
              background: "var(--donut-tip-bg)",
              color: "var(--donut-tip-text)",
              border: "1px solid var(--donut-tip-border)",
              boxShadow: "var(--donut-tip-shadow)",
              minWidth: 140,
              fontSize: 12,
              lineHeight: 1.25,
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 4 }}>{tip.label}</div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <span style={{ opacity: 0.85 }}>{t?.("dashboard_value") || "Value"}</span>
              <span style={{ fontWeight: 750 }}>{toNum(tip.value).toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 2 }}>
              <span style={{ opacity: 0.85 }}>{t?.("dashboard_share") || "Share"}</span>
              <span style={{ fontWeight: 750 }}>{toNum(tip.pct).toFixed(1)}%</span>
            </div>
          </div>
        ) : null}

        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={styles.donutSvg}>
          <defs>
            <radialGradient id="gloss" cx="35%" cy="30%" r="70%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
              <stop offset="45%" stopColor="rgba(255,255,255,0.05)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>

            <filter id="segGlow" x="-60%" y="-60%" width="220%" height="220%">
              <feDropShadow dx="0" dy="0" stdDeviation="2.4" floodColor="rgba(255,255,255,0.16)" />
              <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="rgba(0,0,0,0.25)" />
            </filter>
          </defs>

          <circle cx={cx} cy={cy} r={rInner - 8} fill="var(--donut-hole)" opacity="1" />

          {segs.map((s) => {
            const isHover = hoverIdx === s.idx;
            const { dx, dy } = isHover ? explode(s.mid, 10) : { dx: 0, dy: 0 };

            return (
              <g
                key={`seg-${s.idx}`}
                onMouseEnter={(e) => {
                  setHoverIdx(s.idx);
                  updateTip(e, s.idx);
                }}
                onMouseMove={(e) => updateTip(e, s.idx)}
                onMouseLeave={hideTip}
                style={{ cursor: "pointer" }}
              >
                <path
                  d={s.path}
                  fill={s.color}
                  opacity={isHover ? 1 : 0.92}
                  filter={isHover ? "url(#segGlow)" : "none"}
                  className={styles.donutSeg}
                  style={{ transform: `translate(${dx}px, ${dy}px)` }}
                />
                <path
                  d={s.path}
                  fill="url(#gloss)"
                  opacity={0.85}
                  style={{ transform: `translate(${dx}px, ${dy}px)` }}
                />
              </g>
            );
          })}

          <text x="50%" y="47%" textAnchor="middle" className={styles.donutCenterVal}>
            {toNum(total).toLocaleString()}
          </text>
          <text x="50%" y="61%" textAnchor="middle" className={styles.donutCenterLbl}>
            {t?.("dashboard_total") || "total"}
          </text>
        </svg>

        <div className={styles.legend}>
          {items.map((it, idx) => (
            <div
              key={idx}
              className={styles.legendRow}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx(null)}
            >
              <span className={styles.legendDot} style={{ background: colors[idx % 4] }} />
              <span className={styles.legendLbl}>{it.label}</span>
              <span className={styles.legendVal}>{toNum(it.value).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Bars({ title, points, maxBars = 6, t }) {
  const data = (points || []).slice(0, maxBars);
  const max = Math.max(1, ...data.map((x) => toNum(x.value)));

  return (
    <div className={`${styles.card} ${styles.stretchCard}`}>
      <div className={styles.cardHead}>
        <div className={styles.cardTitle}>{title}</div>
      </div>

      <div className={styles.bars}>
        {data.length === 0 ? (
          <div className={styles.emptyMini}>{t?.("dashboard_empty") || "Empty"}</div>
        ) : (
          data.map((p, idx) => (
            <div key={`${p.key}-${idx}`} className={styles.barRow} title={`${p.key}: ${p.value}`}>
              <div className={styles.barKey}>{String(p.key)}</div>
              <div className={styles.barTrack}>
                <div className={styles.barFill} style={{ width: `${Math.round((toNum(p.value) / max) * 100)}%` }} />
              </div>
              <div className={styles.barVal}>{toNum(p.value).toLocaleString()}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* -------------------- main -------------------- */
export default function DashboardPage() {
  const { t } = useTranslation();

  const presets = useMemo(
    () => [
      { label: t("dashboard_last_7_days") || "Last 7 days", days: 7 },
      { label: t("dashboard_last_30_days") || "Last 30 days", days: 30 },
      { label: t("dashboard_last_90_days") || "Last 90 days", days: 90 },
    ],
    [t]
  );

  const persisted = useMemo(() => safeParseJSON(localStorage.getItem(LS_KEY), {}), []);

  const [presetDays, setPresetDays] = useState(Number(persisted.presetDays || 30));
  const [dateFrom, setDateFrom] = useState(persisted.dateFrom || lastNDaysRange(30).from);
  const [dateTo, setDateTo] = useState(persisted.dateTo || lastNDaysRange(30).to);

  const [ownerUiId, setOwnerUiId] = useState(String(persisted.ownerUiId || ""));

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [dash, setDash] = useState(null);

  const [activityQuery, setActivityQuery] = useState("");
  const [activityQueryDebounced, setActivityQueryDebounced] = useState("");
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(12);

  const [activityLoading, setActivityLoading] = useState(false);
  const [activityErr, setActivityErr] = useState("");
  const [activityItems, setActivityItems] = useState([]);
  const [activityMeta, setActivityMeta] = useState({ page: 1, page_size: 12, total: 0, pages: 1 });

  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersErr, setOwnersErr] = useState("");
  const [owners, setOwners] = useState([]);

  const userRole = useMemo(() => String(getUserRole() || "").toLowerCase(), []);
  const userPermissions = useMemo(() => getUserPermissions(), []);

  const canManageRateLimits = userPermissions.includes("system.rate_limits.update");
  const canViewRateLimitStats = userPermissions.includes("system.rate_limits.view");
  const showRateLimitAdminPanel =
    canManageRateLimits ||
    canViewRateLimitStats ||
    userRole === "admin" ||
    userRole === "superadmin";

  useEffect(() => {
    const tmr = setTimeout(() => setActivityQueryDebounced(activityQuery), 350);
    return () => clearTimeout(tmr);
  }, [activityQuery]);

  useEffect(() => {
    const r = lastNDaysRange(presetDays);
    setDateFrom(r.from);
    setDateTo(r.to);
  }, [presetDays]);

  useEffect(() => {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({
          presetDays,
          dateFrom,
          dateTo,
          ownerUiId,
        })
      );
    } catch {}
  }, [presetDays, dateFrom, dateTo, ownerUiId]);

  const dashboardParams = useMemo(() => {
    const params = {};
    if (dateFrom) params.date_from = `${dateFrom}T00:00:00`;
    if (dateTo) params.date_to = `${dateTo}T23:59:59`;
    if (ownerUiId) params.owner_id = ownerUiId;
    return params;
  }, [dateFrom, dateTo, ownerUiId]);

  const activityParams = useMemo(() => {
    const params = {
      page: activityPage,
      page_size: activityPageSize,
    };

    const q = (activityQueryDebounced || "").trim();
    if (q) params.q = q;

    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    if (ownerUiId) params.owner_ui_id = ownerUiId;

    return params;
  }, [activityPage, activityPageSize, activityQueryDebounced, dateFrom, dateTo, ownerUiId]);

  const loadDashboard = async () => {
    setLoading(true);
    setErr("");
    try {
      const dashRes = await api.get("/dashboard", { params: dashboardParams });
      setDash(dashRes.data || null);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.detail || e?.message || (t("dashboard_failed_load") || "Failed to load dashboard"));
      setDash(null);
    } finally {
      setLoading(false);
    }
  };

  const activityReqSeq = useRef(0);

  const loadActivity = async () => {
    const seq = ++activityReqSeq.current;
    setActivityLoading(true);
    setActivityErr("");

    try {
      const res = await api.get("/auth/activityLog/feed", { params: activityParams });
      if (seq !== activityReqSeq.current) return;

      const data = res.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      const meta = data.meta || {};

      setActivityItems(items);
      setActivityMeta({
        page: toNum(meta.page) || activityPage,
        page_size: toNum(meta.page_size) || activityPageSize,
        total: toNum(meta.total) || 0,
        pages: toNum(meta.pages) || 1,
      });
    } catch (e) {
      console.error(e);
      if (seq !== activityReqSeq.current) return;
      setActivityErr(
        e?.response?.data?.detail || e?.message || (t("dashboard_failed_activity") || "Failed to load activity")
      );
      setActivityItems([]);
      setActivityMeta({ page: 1, page_size: activityPageSize, total: 0, pages: 1 });
    } finally {
      if (seq === activityReqSeq.current) setActivityLoading(false);
    }
  };

  const loadOwners = async () => {
    setOwnersLoading(true);
    setOwnersErr("");
    try {
      const pageSize = 200;
      const maxPages = 20;
      let all = [];
      for (let i = 0; i < maxPages; i++) {
        const res = await api.get("/auth/user/all", { params: { skip: i * pageSize, limit: pageSize } });
        const chunk = Array.isArray(res.data) ? res.data : [];
        all = all.concat(chunk);
        if (chunk.length < pageSize) break;
      }

      const mapped = all
        .map((u) => {
          const ui_id = u?.ui_id ?? null;
          const full_name =
            u?.first_name || u?.last_name
              ? `${u?.first_name || ""} ${u?.last_name || ""}`.trim()
              : u?.full_name || "";
          const email = u?.email || "";
          const userId = u?.userId ?? u?.id ?? null;
          return { ui_id, full_name, email, userId };
        })
        .filter((x) => x.ui_id != null);

      const seen = new Set();
      const uniq = [];
      for (const x of mapped) {
        const k = String(x.ui_id);
        if (seen.has(k)) continue;
        seen.add(k);
        uniq.push(x);
      }
      uniq.sort((a, b) => toNum(a.ui_id) - toNum(b.ui_id));

      setOwners(uniq);
    } catch (e) {
      console.error(e);
      setOwnersErr(e?.response?.data?.detail || e?.message || (t("dashboard_failed_owners") || "Failed to load owners"));
      setOwners([]);
    } finally {
      setOwnersLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    loadOwners();
  }, []);

  useEffect(() => {
    loadActivity();
  }, [activityParams]);

  useEffect(() => {
    setActivityPage(1);
  }, [activityQueryDebounced, activityPageSize, dateFrom, dateTo, ownerUiId]);

  const resetFilters = () => {
    setPresetDays(30);
    const r = lastNDaysRange(30);
    setDateFrom(r.from);
    setDateTo(r.to);
    setOwnerUiId("");
    setActivityQuery("");
    setActivityPage(1);
    setActivityPageSize(12);

    setTimeout(() => {
      loadDashboard();
      loadActivity();
    }, 0);
  };

  const applyAll = () => {
    loadDashboard();
    loadActivity();
  };

  const kpi = dash?.kpi || {};

  const dealsStatusItems = useMemo(
    () => [
      { label: t("dashboard_open") || "open", value: toNum(kpi.deals_open) },
      { label: t("dashboard_won") || "won", value: toNum(kpi.deals_won) },
      { label: t("dashboard_lost") || "lost", value: toNum(kpi.deals_lost) },
    ],
    [kpi.deals_open, kpi.deals_won, kpi.deals_lost, t]
  );

  const tasksStatusItems = useMemo(
    () => [
      { label: t("dashboard_done") || "done", value: toNum(kpi.tasks_done) },
      { label: t("dashboard_pending") || "pending", value: toNum(kpi.tasks_pending) },
      { label: t("dashboard_overdue") || "overdue", value: toNum(kpi.tasks_overdue) },
    ],
    [kpi.tasks_done, kpi.tasks_pending, kpi.tasks_overdue, t]
  );

  const activityTotal = toNum(activityMeta.total);
  const activityTotalPages = Math.max(1, toNum(activityMeta.pages) || 1);
  const page = Math.min(activityPage, activityTotalPages);

  const activityFrom = activityTotal === 0 ? 0 : (page - 1) * activityPageSize + 1;
  const activityTo = Math.min(activityTotal, page * activityPageSize);

  const s = dash?.series || [];
  const sparkLeads = useMemo(() => s.map((x) => toNum(x.leads_created)), [s]);
  const sparkTasks = useMemo(() => s.map((x) => toNum(x.tasks_created)), [s]);
  const sparkWon = useMemo(() => s.map((x) => toNum(x.deals_won_amount)), [s]);

  const dLeads = useMemo(() => calcDeltaPct(sparkLeads), [sparkLeads]);
  const dTasks = useMemo(() => calcDeltaPct(sparkTasks), [sparkTasks]);
  const dWon = useMemo(() => calcDeltaPct(sparkWon), [sparkWon]);

  return (
    <div className={styles.page}>
      <div className={`container-fluid ${styles.containerRoot}`}>
        <div className="row">
          <div data-academy="dashboard-header" className={`col-12 d-flex align-items-end justify-content-between gap-3 ${styles.mobileHeaderRow}`}>
            <div className={styles.headerLeft}>
              <div className={styles.h1}>{t("dashboard_title") || "Dashboard"}</div>
              <div className={styles.sub}>{t("dashboard_subtitle") || "Analytics overview"}</div>
            </div>

            <div className={`d-flex gap-2 align-items-center ${styles.mobileHeaderActions}`}>
              <button className="btn btn-primary" onClick={applyAll} disabled={loading || activityLoading}>
                {loading || activityLoading ? `${t("loading") || "Loading"}...` : t("refresh") || "Refresh"}
              </button>
              <button className="btn btn-outline-secondary" onClick={resetFilters} disabled={loading || activityLoading}>
                {t("reset") || "Reset"}
              </button>
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12">
            <div data-academy="dashboard-filters" className={styles.filtersCard}>
              <div className="row g-2 align-items-end">
                <div className="col-12 col-md-3">
                  <div className={styles.label}>{t("dashboard_date_range") || "Date range"}</div>
                  <select
                    className="form-select"
                    value={presetDays}
                    onChange={(e) => setPresetDays(Number(e.target.value))}
                    disabled={loading || activityLoading}
                  >
                    {presets.map((p) => (
                      <option key={p.days} value={p.days}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-6 col-md-3">
                  <div className={styles.label}>{t("from") || "From"}</div>
                  <input
                    type="date"
                    className="form-control"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    disabled={loading || activityLoading}
                  />
                </div>

                <div className="col-6 col-md-3">
                  <div className={styles.label}>{t("to") || "To"}</div>
                  <input
                    type="date"
                    className="form-control"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    disabled={loading || activityLoading}
                  />
                </div>

                <div className="col-8 col-md-2">
                  <OwnerSearchSelect
                    t={t}
                    label={t("dashboard_owner_id") || "Owner ID"}
                    placeholder={t("dashboard_owner_select") || "Select..."}
                    options={owners}
                    value={ownerUiId}
                    onChange={(v) => setOwnerUiId(v)}
                    disabled={loading || activityLoading}
                    loading={ownersLoading}
                    errorText={ownersErr}
                  />
                </div>

                <div className="col-4 col-md-1 d-grid">
                  <button className="btn btn-primary" onClick={applyAll} disabled={loading || activityLoading}>
                    {t("apply") || "Apply"}
                  </button>
                </div>
              </div>

              {err ? <div className={styles.errorBox}>{String(err)}</div> : null}
            </div>
          </div>
        </div>

        <div data-academy="dashboard-kpi" className="row g-2 mt-2">
          <div className="col-12">
            <div className="row g-2">
              <div className="col-12 col-md-6 col-xl-3">
                <SparkKpi
                  t={t}
                  title={t("dashboard_leads") || "Leads"}
                  value={toNum(kpi.leads_total).toLocaleString()}
                  deltaPct={dLeads}
                  data={sparkLeads}
                  colorVar="--c1"
                />
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <SparkKpi
                  t={t}
                  title={t("dashboard_won_eur") || "Won €"}
                  value={toNum(kpi.revenue_won).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  suffix="€"
                  deltaPct={dWon}
                  data={sparkWon}
                  colorVar="--c2"
                />
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <SparkKpi
                  t={t}
                  title={t("dashboard_tasks") || "Tasks"}
                  value={toNum(kpi.tasks_total).toLocaleString()}
                  deltaPct={dTasks}
                  data={sparkTasks}
                  colorVar="--c3"
                />
              </div>

              <div className="col-12 col-md-6 col-xl-3">
                <SparkKpi
                  t={t}
                  title={t("dashboard_win_rate") || "Win rate"}
                  value={toNum(dash?.meta?.conversion_rate_percent)}
                  suffix="%"
                  deltaPct={null}
                  data={sparkWon}
                  colorVar="--c4"
                />
              </div>
            </div>
          </div>
        </div>

        <div className={`row g-3 mt-1 ${styles.mainRow}`}>
          <div data-academy="dashboard-charts" className={`col-12 col-xl-8 d-flex flex-column gap-3 ${styles.colMin0}`}>
            <div className="row g-2 align-items-stretch">
              <div className="col-12 col-lg-6 d-flex">
                <AreaOverview t={t} title={t("dashboard_overview") || "Overview"} series={dash?.series || []} />
              </div>
              <div className="col-12 col-lg-3 d-flex">
                <Donut
                  t={t}
                  title={t("dashboard_deals_status") || "Deals status"}
                  total={toNum(kpi.deals_total)}
                  items={dealsStatusItems}
                />
              </div>
              <div className="col-12 col-lg-3 d-flex">
                <Donut
                  t={t}
                  title={t("dashboard_tasks_status") || "Tasks status"}
                  total={toNum(kpi.tasks_total)}
                  items={tasksStatusItems}
                />
              </div>
            </div>

            <div className="row g-2 align-items-stretch">
              <div className="col-12 col-lg-6 d-flex">
                <Bars t={t} title={t("dashboard_deals_by_stage") || "Deals by stage"} points={dash?.deals_by_stage || []} />
              </div>
              <div className="col-12 col-lg-6 d-flex">
                <Bars t={t} title={t("dashboard_leads_by_source") || "Leads by source"} points={dash?.leads_by_source || []} />
              </div>
            </div>
          </div>

          <div data-academy="dashboard-activity" className={`col-12 col-xl-4 d-flex flex-column ${styles.colMin0} ${styles.rightCol} ${styles.rightColClamp}`}>
            <div className={`${styles.card} ${styles.activityCard}`}>
              <div className={styles.cardHead}>
                <div className={styles.activityHeadLeft}>
                  <div className={styles.cardTitle}>{t("dashboard_recent_activity") || "Recent activity"}</div>
                  <div className={styles.smallMuted}>{t("dashboard_tenant_wide") || "Tenant-wide"}</div>
                </div>

                <div className={styles.activityHeadRight}>
                  <input
                    type="text"
                    className={`form-control ${styles.activitySearch}`}
                    placeholder={t("dashboard_search_activity") || "Search activity…"}
                    value={activityQuery}
                    onChange={(e) => setActivityQuery(e.target.value)}
                    disabled={activityLoading}
                  />
                </div>
              </div>

              <div className={styles.activityBody}>
                {activityErr ? <div className={styles.errorBox}>{String(activityErr)}</div> : null}

                <table className={`table table-sm table-hover table-striped mb-0 ${styles.activityTable}`}>
                  <thead className="thead-dark">
                    <tr>
                      <th className={styles.colId}>{t("dashboard_ui_id") || "ID"}</th>
                      <th className={styles.colAction}>{t("dashboard_action") || "Action"}</th>
                      <th className={styles.colEntity}>{t("dashboard_entity") || "Entity"}</th>
                      <th className={styles.colDesc}>{t("dashboard_description") || "Description"}</th>
                      <th className={styles.colCreated}>{t("dashboard_created") || "Created"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityItems.map((a) => {
                      const action =
                        t(`activity_action_${stripEnumPrefix(a.action).toLowerCase()}`) || prettyAction(a.action, t);
                      const entity =
                        t(`activity_entity_${stripEnumPrefix(a.entity).toLowerCase()}`) || prettyEntity(a.entity, t);
                      const desc = a.description || "-";

                      const shownId = a.ui_id ?? a.id;
                      const pkId = a.id;

                      return (
                        <tr key={pkId}>
                          <td className={styles.mono} title={`PK: ${pkId}`}>
                            {shownId}
                          </td>
                          <td title={String(a.action || "")}>{action}</td>
                          <td title={String(a.entity || "")}>{entity}</td>
                          <td className={styles.descCell} title={desc}>
                            {desc}
                          </td>
                          <td className={styles.mono}>{formatDateTime(a.created_at, t)}</td>
                        </tr>
                      );
                    })}

                    {!activityLoading && activityItems.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-muted">
                          {t("dashboard_empty") || "Empty"}
                        </td>
                      </tr>
                    ) : null}

                    {activityLoading ? (
                      <tr>
                        <td colSpan={5} className="text-muted">
                          {(t("loading") || "Loading") + "..."}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <div className={styles.activityFooter}>
                <div className={styles.activityMeta}>
                  {toNum(activityMeta.total) ? (
                    <>
                      {t("showing_entries", {
                        start: activityFrom,
                        end: activityTo,
                        total: toNum(activityMeta.total),
                      }) ||
                        `Showing ${activityFrom}–${activityTo} of ${toNum(activityMeta.total)}`}
                    </>
                  ) : (
                    t("dashboard_no_rows") || "No rows"
                  )}
                </div>

                <div className={styles.activityControls}>
                  <select
                    className={`form-select ${styles.pageSizeSelect}`}
                    value={activityPageSize}
                    onChange={(e) => setActivityPageSize(Number(e.target.value))}
                    disabled={activityLoading}
                    title={t("rows_per_page") || "Rows per page"}
                  >
                    <option value={8}>8</option>
                    <option value={12}>12</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>

                  <button
                    className={`btn btn-outline-secondary ${styles.pageBtn}`}
                    onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                    disabled={activityLoading || page <= 1}
                  >
                    {t("previous") || "Prev"}
                  </button>

                  <div className={styles.pageInfo}>
                    {page} / {Math.max(1, toNum(activityMeta.pages) || 1)}
                  </div>

                  <button
                    className={`btn btn-outline-secondary ${styles.pageBtn}`}
                    onClick={() => setActivityPage((p) => Math.min(Math.max(1, toNum(activityMeta.pages) || 1), p + 1))}
                    disabled={activityLoading || page >= Math.max(1, toNum(activityMeta.pages) || 1)}
                  >
                    {t("next") || "Next"}
                  </button>
                </div>
              </div>

              <div className={styles.footerNote}>
                {t("dashboard_footer_note", {
                  dash: "/dashboard",
                  activity: "/auth/activityLog/feed",
                }) || (
                  <>
                    {t("dashboard_series_from") || "Series from"} <code>/dashboard</code>.{" "}
                    {t("dashboard_activity_from") || "Activity from"} <code>/auth/activityLog/feed</code>.
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {showRateLimitAdminPanel && (
          <div className="row g-3 mb-3">
            <div className="col-12">
              <div className={`${styles.card} ${styles.rateLimitAdminCard}`}>
                <div className={styles.cardHead}>
                  <div>
                    <div className={styles.cardTitle}>
                      {t("dashboard_rate_limiting_admin") || "Rate Limiting Admin"}
                    </div>
                    <div className={styles.smallMuted}>
                      {t("dashboard_rate_limiting_admin_subtitle") ||
                        "Quick access to tenant rate limit controls and monitoring."}
                    </div>
                  </div>
                  <div className={styles.rateLimitAdminActions}>
                    {canManageRateLimits ? (
                      <Link to="/menu/admin/rate-limiting" className="btn btn-sm btn-primary">
                        {t("dashboard_rate_limiting_manage") || "Manage"}
                      </Link>
                    ) : null}
                    {canViewRateLimitStats ? (
                      <Link
                        to="/menu/admin/rate-limiting/stats"
                        className="btn btn-sm btn-outline-secondary"
                      >
                        {t("dashboard_rate_limiting_stats") || "View Stats"}
                      </Link>
                    ) : null}
                    {!canManageRateLimits && !canViewRateLimitStats ? (
                      <div className={styles.smallMuted}>
                        {t("dashboard_rate_limiting_access_help") ||
                          "Admin access not enabled yet. Contact SuperAdmin for permissions."}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rate Limiting Info Widget */}
        <div className="row g-3 mt-3">
          <div className="col-12">
            <RateLimitsInfo />
          </div>
        </div>
      </div>
    </div>
  );
}